import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import ProfileInfoSection from '../../../shared-components/ProfileInfoSection';
import {useGetStaffCategoriesQuery} from '../../../api/services/lookupsService';
import {
    useGetEmployeeStaffCategoriesQuery,
    useGetMyStaffCategoriesQuery,
    useUpdateEmployeeStaffCategoriesMutation
} from '../../../api/services/employeeService';
import useNotification from '../../../utility/hooks/useNotification';
import {getStaffSectionAccess, STAFF_CATEGORIES_MODULE, STAFF_SECTION_ACCESS} from './staffProfileSectionAccess';

const EMPTY = [];

// the heading of the other staff sections, with Edit on the same line
const SECTION_HEADING_SX = {fontSize: 15, fontWeight: 'bold'};

const Heading = ({action}) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75} minHeight={30}>
        <Typography variant="body2" sx={SECTION_HEADING_SX}>Staff Categories</Typography>
        {action}
    </Box>
);

Heading.propTypes = {
    action: PropTypes.node
};

const nameOf = category => category.parentName ? `${category.name} (${category.parentName})` : category.name;

// AD-2790: the staff categories ticked on a member of staff, read only.
const TickedList = ({ticked}) => (
    ticked.length === 0
        ? <Typography variant="body2" color="text.secondary" data-testid="staff-categories-none">None</Typography>
        : (
            <Box component="ul" sx={{m: 0, pl: 2.5}} data-testid="staff-categories-ticked">
                {ticked.map(category => (
                    <Typography component="li" variant="body2" key={category.id}>{nameOf(category)}</Typography>
                ))}
            </Box>
        )
);

TickedList.propTypes = {
    ticked: PropTypes.arrayOf(PropTypes.object).isRequired
};

// AD-2790: in MY PROFILE a member of staff sees their own categories and cannot change them. Nothing shows until a
// category has been ticked for them.
const MyStaffCategories = ({permissions}) => {
    const hidden = getStaffSectionAccess(permissions, STAFF_CATEGORIES_MODULE) === STAFF_SECTION_ACCESS.hide;
    const {data: ticked = EMPTY} = useGetMyStaffCategoriesQuery(undefined, {skip: hidden});

    if (hidden || ticked.length === 0) {
        return null;
    }

    return (
        <Box data-testid="staff-categories-section">
            <ProfileInfoSection>
                <Heading/>
                <TickedList ticked={ticked}/>
            </ProfileInfoSection>
        </Box>
    );
};

MyStaffCategories.propTypes = {
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired
};

// AD-2790: on a staff profile a School Admin ticks the school's categories. Nothing shows until the school has made
// a category on the Lookups tab.
const AdminStaffCategories = ({employeeId, permissions}) => {
    const access = getStaffSectionAccess(permissions, STAFF_CATEGORIES_MODULE);
    const hidden = access === STAFF_SECTION_ACCESS.hide;
    const {data: categories = EMPTY} = useGetStaffCategoriesQuery(undefined, {skip: hidden});
    const {data: ticked = EMPTY} = useGetEmployeeStaffCategoriesQuery(employeeId, {skip: hidden || !employeeId});
    const [update, {isLoading: isSaving}] = useUpdateEmployeeStaffCategoriesMutation();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const [isEditing, setIsEditing] = useState(false);
    const [chosen, setChosen] = useState([]);

    useEffect(() => {
        setChosen(ticked.map(category => category.id));
    }, [ticked, isEditing]);

    if (hidden || categories.length === 0) {
        return null;
    }

    const toggle = id => setChosen(chosen.includes(id) ? chosen.filter(c => c !== id) : [...chosen, id]);
    const topLevel = categories.filter(c => !c.parentId);

    const handleSave = () => {
        update({staffId: employeeId, staffCategoryIds: chosen})
            .unwrap()
            .then(() => {
                showSuccessNotification('Staff categories updated successfully');
                setIsEditing(false);
            })
            .catch(() => showErrorNotification('Failed to update the staff categories'));
    };

    return (
        <Box data-testid="staff-categories-section">
            <ProfileInfoSection>
                <Heading action={!isEditing && access === STAFF_SECTION_ACCESS.edit && (
                    <Button size="small" sx={{width: 110}} variant="contained"
                        onClick={() => setIsEditing(true)} data-testid="staff-categories-edit">
                        Edit
                    </Button>
                )}/>
                {isEditing
                    ? (
                        <Box>
                            {topLevel.map(parent => (
                                <Box key={parent.id}>
                                    <FormControlLabel
                                        sx={{ml: -0.5}}
                                        control={<Checkbox size="small" sx={{p: 0.5, mr: 0.5}} checked={chosen.includes(parent.id)}
                                            onChange={() => toggle(parent.id)}/>}
                                        label={<Typography variant="body2">{parent.name}</Typography>}
                                        data-testid={`staff-category-tick-${parent.id}`}
                                    />
                                    {categories.filter(c => c.parentId === parent.id).map(child => (
                                        <Box key={child.id} sx={{pl: 3}}>
                                            <FormControlLabel
                                                sx={{ml: -0.5}}
                                                control={<Checkbox size="small" sx={{p: 0.5, mr: 0.5}} checked={chosen.includes(child.id)}
                                                    onChange={() => toggle(child.id)}/>}
                                                label={<Typography variant="body2">{child.name}</Typography>}
                                                data-testid={`staff-category-tick-${child.id}`}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                            <Box display="flex" gap={1.5} mt={1.5}>
                                <Button size="small" sx={{width: 110}} variant="contained" disabled={isSaving}
                                    onClick={handleSave} data-testid="staff-categories-save">
                                    Save
                                </Button>
                                <Button size="small" sx={{width: 110}} variant="outlined" onClick={() => setIsEditing(false)}>
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    )
                    : <TickedList ticked={ticked}/>}
            </ProfileInfoSection>
        </Box>
    );
};

AdminStaffCategories.propTypes = {
    employeeId: PropTypes.number,
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired
};

const StaffCategoriesSection = ({self, employeeId, permissions}) =>
    self
        ? <MyStaffCategories permissions={permissions}/>
        : <AdminStaffCategories employeeId={employeeId} permissions={permissions}/>;

StaffCategoriesSection.propTypes = {
    self: PropTypes.bool,
    employeeId: PropTypes.number,
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default StaffCategoriesSection;
